import React, { useState, useEffect, useCallback } from 'react';

import type { GroupByValue } from '../../CoursesLessonsPage.grouping.config';
import type { LessonGroup } from '../../CoursesLessonsPage.grouping';

import CoursesLessonsGroupedTable from './CoursesLessonsGroupedTable';

type TableColumn = {
  key: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  sortable?: boolean;
  justifyContent?: string;
};

type CellRendererProps = {
  item: any;
  key: string;
  minWidth?: number;
  maxWidth?: number;
  itemIndex?: number;
  className?: string;
};

const CoursesLessonsGroupedTableContainer = ({
  groupBy,
  columns,
  params,
  lessonGroups,
  showGroupHeaders,
  actions,
  cellRenderer,
  onSortChange,
  onRowClick,
}: {
  groupBy?: GroupByValue;
  columns: TableColumn[];
  params: Record<string, any>;
  lessonGroups: LessonGroup[];
  showGroupHeaders: boolean;
  actions?: {
    key: string;
    icon: string;
    tooltipText: string;
    onClick: (item: any) => void;
  }[];
  cellRenderer: (props: CellRendererProps, DefaultCell: any) => React.ReactNode;
  onSortChange: (sortKey: string) => void;
  onRowClick: (item: any) => void;
}) => {
  const [collapsedGroupIds, setCollapsedGroupIds] = useState(
    () => new Set<string>(),
  );

  useEffect(() => {
    setCollapsedGroupIds(new Set());
  }, [groupBy]);

  const isGroupExpanded = useCallback(
    (groupId: string) => !collapsedGroupIds.has(groupId),
    [collapsedGroupIds],
  );

  const handleGroupHeaderClick = useCallback((groupId: string) => {
    setCollapsedGroupIds((previousCollapsedGroupIds) => {
      const nextCollapsedGroupIds = new Set(previousCollapsedGroupIds);

      if (nextCollapsedGroupIds.has(groupId)) {
        nextCollapsedGroupIds.delete(groupId);
      } else {
        nextCollapsedGroupIds.add(groupId);
      }

      return nextCollapsedGroupIds;
    });
  }, []);

  return (
    <CoursesLessonsGroupedTable
      columns={columns}
      params={params}
      lessonGroups={lessonGroups}
      showGroupHeaders={showGroupHeaders}
      actions={actions}
      cellRenderer={cellRenderer}
      onSortChange={onSortChange}
      onRowClick={onRowClick}
      isGroupExpanded={isGroupExpanded}
      onGroupHeaderClick={handleGroupHeaderClick}
    />
  );
};

export default React.memo(CoursesLessonsGroupedTableContainer) as any;
